import { useMemo, useState } from "react";

import type { StructuredProduct } from "@/types/domain";
import {
  capitalize,
  mergeProducts,
  normalizeText,
  productHeadline,
} from "./lineHelpers";

/**
 * Lo que eligió en "¿Qué vendes?" / "¿Qué obras haces?" y el nombre que tendrá su
 * línea: el que sugiere la IA y, si solo eligió chips, el del chip.
 */
export function useProductSelection() {
  const [products, setProducts] = useState<StructuredProduct[]>([]);
  const [aiName, setAiName] = useState("");
  const [chipName, setChipName] = useState("");

  function add(incoming: StructuredProduct[], name?: { value: string; fromAi: boolean }) {
    setProducts((current) => mergeProducts(current, incoming));
    if (name?.fromAi && !aiName) setAiName(name.value);
    if (name && !name.fromAi && !chipName) setChipName(name.value);
  }

  function remove(removedTerms: string[]) {
    const removed = new Set(removedTerms.map(normalizeText));
    const remaining = products.filter((product) => !removed.has(normalizeText(product.term)));
    setProducts(remaining);
    // Sin productos, ningún nombre elegido describe ya la línea.
    if (remaining.length === 0) {
      setAiName("");
      setChipName("");
    }
  }

  const terms = useMemo(() => products.map((product) => product.term), [products]);

  return {
    products,
    terms,
    add,
    remove,
    headline: productHeadline(products),
    lineName: aiName || chipName || capitalize(products[0]?.label ?? "Mi línea de negocio"),
  };
}
